import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { receiptApi, tenantApi } from '../services/api';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/currency';
import {
  PlusIcon,
  ArrowUpTrayIcon,
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  DocumentMagnifyingGlassIcon,
  PencilSquareIcon,
  EnvelopeIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const Receipts = () => {
  const { tenantId } = useParams();
  const [receipts, setReceipts] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const [markSentModalOpen, setMarkSentModalOpen] = useState(false);
  const [markSentReceipt, setMarkSentReceipt] = useState(null);
  const [markSentDate, setMarkSentDate] = useState('');
  const [markSentEmail, setMarkSentEmail] = useState('');
  const [savingMarkSent, setSavingMarkSent] = useState(false);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      let response;
      if (tenantId) {
        response = await receiptApi.getByTenant(tenantId);
        const tenantResponse = await tenantApi.getById(tenantId);
        setTenant(tenantResponse.data);
      } else {
        response = await receiptApi.getAll();
      }
      setReceipts(response.data);
    } catch (error) {
      toast.error('Failed to load receipts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProblems = async () => {
    try {
      const response = await receiptApi.getProblems();
      const all = response.data || [];
      // If on a tenant page, only show problems for that tenant
      const filtered = tenantId
        ? all.filter(p => p.receipts.some(r => String(r.tenantId) === String(tenantId)))
        : all;
      setProblems(filtered);
    } catch (error) {
      console.error('Failed to load problems', error);
    }
  };

  useEffect(() => {
    fetchReceipts();
    fetchProblems();
  }, [tenantId]);

  const handleDownload = async (id) => {
    try {
      await receiptApi.download(id);
    } catch (error) {
      toast.error('Failed to download receipt');
      console.error(error);
    }
  };

  const openEmailModal = (receipt) => {
    setSelectedReceipt(receipt);
    setEmailAddress(receipt.tenant?.email || tenant?.email || '');
    setEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    setEmailModalOpen(false);
    setSelectedReceipt(null);
    setEmailAddress('');
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailAddress || !selectedReceipt) return;
    setSendingEmail(true);
    try {
      await receiptApi.sendEmail(selectedReceipt.id, emailAddress);
      toast.success(`Receipt sent successfully to ${emailAddress}`);
      closeEmailModal();
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send receipt');
      console.error(error);
    } finally {
      setSendingEmail(false);
    }
  };

  const openMarkSentModal = (receipt) => {
    setMarkSentReceipt(receipt);
    const existing = receipt.sentByEmailAt
      ? format(new Date(receipt.sentByEmailAt), "yyyy-MM-dd'T'HH:mm")
      : format(new Date(), "yyyy-MM-dd'T'HH:mm");
    setMarkSentDate(existing);
    setMarkSentEmail(receipt.sentByEmailTo || receipt.tenant?.email || tenant?.email || '');
    setMarkSentModalOpen(true);
  };

  const closeMarkSentModal = () => {
    setMarkSentModalOpen(false);
    setMarkSentReceipt(null);
    setMarkSentDate('');
    setMarkSentEmail('');
  };

  const handleMarkSent = async (e) => {
    e.preventDefault();
    if (!markSentReceipt) return;
    setSavingMarkSent(true);
    try {
      await receiptApi.updateSentStatus(markSentReceipt.id, {
        sentByEmailAt: markSentDate ? new Date(markSentDate).toISOString() : null,
        sentByEmailTo: markSentEmail,
      });
      toast.success('Receipt marked as sent');
      closeMarkSentModal();
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update receipt');
      console.error(error);
    } finally {
      setSavingMarkSent(false);
    }
  };

  const handleDelete = async (receipt, force = false) => {
    if (receipt.isSigned && !force) {
      const confirmed = window.confirm(
        `⚠️ WARNING: Receipt ${receipt.receiptNo} is signed.\n\nDeleting a signed receipt is irreversible and removes the legal document.\n\nAre you absolutely sure you want to delete it?`
      );
      if (!confirmed) return;
      return handleDelete(receipt, true);
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete receipt ${receipt.receiptNo}?`
    );
    if (!confirmed) return;

    try {
      await receiptApi.delete(receipt.id, force);
      toast.success('Receipt deleted successfully');
      fetchReceipts();
      fetchProblems();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete receipt');
      console.error(error);
    }
  };

  // Each receipt carries its tenant (preloaded by the API), which is the only
  // source of currency on the "All Receipts" page where no tenant is selected
  const currencyOf = (receipt) => receipt?.tenant?.currency || tenant?.currency || 'USD';

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          {tenantId && tenant ? (
            <div className="flex items-center">
              <Link to="/tenants" className="text-gray-600 hover:text-gray-900 mr-2">
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Receipts for {tenant.firstName} {tenant.lastName}
                </h1>
                <p className="text-gray-600">
                  Monthly Rent: {formatCurrency(tenant.totalRent, tenant.currency || 'USD')} ({formatCurrency(tenant.basicRent, tenant.currency || 'USD')} + {formatCurrency(tenant.charges, tenant.currency || 'USD')})
                </p>
              </div>
            </div>
          ) : (
            <h1 className="text-2xl font-bold text-gray-900">All Receipts</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={tenantId ? `/tenants/${tenantId}/receipts/import` : '/receipts/import'}
            className="btn-secondary inline-flex items-center"
          >
            <ArrowUpTrayIcon className="h-5 w-5 mr-1" />
            Import Receipt
          </Link>
          <Link
            to={tenantId ? `/tenants/${tenantId}/receipts/new` : '/receipts/new'}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Generate Receipt
          </Link>
        </div>
      </div>

      {/* Problems section */}
      {problems.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center mb-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0" />
            <h2 className="text-sm font-semibold text-amber-800">
              {problems.length} issue{problems.length > 1 ? 's' : ''} detected
            </h2>
          </div>
          <div className="space-y-4">
            {problems.map((problem, idx) => (
              <div key={idx}>
                <p className="text-sm font-medium text-amber-700 mb-2">{problem.description}</p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs bg-white rounded border border-amber-200">
                    <thead className="bg-amber-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-amber-700">Receipt No</th>
                        <th className="px-3 py-2 text-left font-medium text-amber-700">Period</th>
                        <th className="px-3 py-2 text-left font-medium text-amber-700">Amount</th>
                        <th className="px-3 py-2 text-left font-medium text-amber-700">Status</th>
                        <th className="px-3 py-2 text-right font-medium text-amber-700">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {problem.receipts.map((r) => (
                        <tr key={r.id} className="hover:bg-amber-50">
                          <td className="px-3 py-2 font-medium text-gray-800">{r.receiptNo}</td>
                          <td className="px-3 py-2 text-gray-600">
                            {formatDate(r.periodStart)} – {formatDate(r.periodEnd)}
                          </td>
                          <td className="px-3 py-2 text-gray-800">
                            {formatCurrency(r.totalAmount, currencyOf(r))}
                          </td>
                          <td className="px-3 py-2">
                            {r.isSigned ? (
                              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-medium">Signed</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium">Unsigned</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => handleDelete(r)}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading receipts...</div>
      ) : receipts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No receipts found</h3>
          <p className="text-gray-600 mb-4">
            {tenantId
              ? `Start by generating your first receipt for ${tenant?.firstName || ''} ${tenant?.lastName || ''}.`
              : 'Start by generating your first receipt.'}
          </p>
          <Link
            to={tenantId ? `/tenants/${tenantId}/receipts/new` : '/receipts/new'}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Generate Receipt
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow sm:rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
                  {!tenantId && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lang</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emailed</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-44">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {receipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-primary-600">
                      {receipt.receiptNo}
                    </td>
                    {!tenantId && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        Tenant #{receipt.tenantId}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(receipt.periodStart)} - {formatDate(receipt.periodEnd)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {formatCurrency(receipt.totalAmount, currencyOf(receipt))}
                      {receipt.isProrated && (
                        <span className="block text-xs font-normal text-gray-500">
                          prorated · {receipt.proratedDays} day{receipt.proratedDays > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(receipt.paymentDate)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {receipt.paymentType}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {receipt.language === 'fr' ? 'FR' : 'EN'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {receipt.isSigned ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Signed</span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Unsigned</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {receipt.sentByEmailAt ? (
                        <span
                          className="inline-flex items-center px-2 text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 cursor-default"
                          title={`Sent to ${receipt.sentByEmailTo} on ${formatDateTime(receipt.sentByEmailAt)}`}
                        >
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          {formatDate(receipt.sentByEmailAt)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium w-44">
                      <div className="flex items-center justify-end gap-1.5">
                        <Link
                          to={`/receipts/${receipt.id}/sign`}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title={receipt.isSigned ? 'View Signed Receipt' : 'Sign Receipt'}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => openEmailModal(receipt)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Send Receipt by Email"
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openMarkSentModal(receipt)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                          title={receipt.sentByEmailAt ? 'Edit sent date' : 'Mark as sent manually'}
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(receipt.id)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Download Receipt"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(receipt.id)}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="View Receipt"
                        >
                          <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(receipt)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title={receipt.isSigned ? 'Delete signed receipt (warning)' : 'Delete Receipt'}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {emailModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeEmailModal}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSendEmail}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                      <EnvelopeIcon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Send Receipt by Email</h3>
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-4">
                          Send receipt <strong>{selectedReceipt?.receiptNo}</strong> via email.
                        </p>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          value={emailAddress}
                          onChange={(e) => setEmailAddress(e.target.value)}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                          placeholder="tenant@example.com"
                          required
                          autoFocus
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={sendingEmail}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                  <button
                    type="button"
                    onClick={closeEmailModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Sent Modal */}
      {markSentModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeMarkSentModal}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleMarkSent}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PaperAirplaneIcon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Mark as Sent</h3>
                      <p className="text-sm text-gray-500 mt-1 mb-4">
                        Record that receipt <strong>{markSentReceipt?.receiptNo}</strong> was sent by email.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sent at</label>
                          <input
                            type="datetime-local"
                            value={markSentDate}
                            onChange={(e) => setMarkSentDate(e.target.value)}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sent to (email)</label>
                          <input
                            type="email"
                            value={markSentEmail}
                            onChange={(e) => setMarkSentEmail(e.target.value)}
                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                            placeholder="tenant@example.com"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={savingMarkSent}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400"
                  >
                    {savingMarkSent ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={closeMarkSentModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;
